<template>
  <div ref="popupWindow" class="popup z-index-10-fg confirmation small-popup edit-details-popup">
    <div ref="popupContent" class="popup-content">
      <div class="popup-header">
        <h2>Edit Details</h2>
        <img src="../assets/close.svg" alt="" class="popup-close" @click="$emit('closePopup')" />
      </div>
      <div class="popup-body">
        <div :class="['primary-input', 'input-item', complete ? 'medium-item' : 'large-item']">
          <input type="text" v-model="jobName" />
          <label>Job Name</label>
        </div>
        <div class="primary-input input-item medium-item">
          <input type="text" v-model="bwQuote" />
          <label>BW Quote</label>
        </div>
        <div class="primary-input input-item medium-item" v-if="!complete">
          <input type="text" v-model="amount" @blur="formatAmount()" />
          <label>Amount</label>
        </div>

        <div class="primary-input input-item large-item">
          <textarea type="text" v-model="notes" rows="4" style="resize: none"></textarea>
          <label>Notes</label>
        </div>
        <Dropdown
          :options="[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]"
          label="Complete"
          v-model="complete"
          v-if="showComplete"
          class="input-item small-item" />

        <div class="primary-input input-item small-item" v-if="complete">
          <input type="text" v-model="invNum" />
          <label>Invoice Number</label>
        </div>
        <div class="primary-input input-item small-item" v-if="complete">
          <input type="text" v-model="amount" @blur="formatAmount()" />
          <label>Amount</label>
        </div>

        <Checkbox label="Delivery" class="input-item large-item" v-model="hasDelivery" />

        <AddressSearch :options="lookupResults" label="Site Address" v-model="deliveryAddress" v-if="hasDelivery" class="input-item large-item" @autofillAddress="(o) => (deliveryAddress = o.formattedAddress)" @clearLookup="lookupResults.length = 0" />

        <div class="primary-input input-item small-item" v-if="hasDelivery">
          <input type="date" v-model="deliveryDate" />
          <label>Delivery Date</label>
        </div>

        <div class="primary-input input-item small-item" v-if="hasDelivery">
          <input type="text" v-model="gateCode" />
          <label>Gate Code</label>
        </div>

        <div class="primary-input input-item large-item" v-if="hasDelivery">
          <textarea type="text" v-model="deliveryNotes" rows="4" style="resize: none"></textarea>
          <label>Delivery Notes</label>
        </div>
      </div>
      <div class="popup-footer">
        <button @click="$emit('closePopup')" class="text-btn">close</button>
        <button :disabled="isSaving" :class="{ 'square-btn': true, saving: isSaving }" @click="saveDetails()">Save<img src="../assets/spinner.svg" /></button>
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import Dropdown from './inputs/Dropdown.vue';
import AddressSearch from './inputs/AddressSearch.vue';
import Checkbox from './inputs/Checkbox.vue';
import useAlerts from '@/composables/useAlerts';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useDecryptSymbols from '@/composables/useDecryptSymbols';
import useLeadMutate from '@/composables/useLeadMutate';
import useAddressLookup from '@/composables/useAddressLookup';

export default {
  name: 'Reminder',
  emits: ['closePopup'],
  components: {
    Dropdown,
    AddressSearch,
    Checkbox,
  },
  props: {
    leadid: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    const { encryptSymbols } = useDecryptSymbols();
    const { updateLead } = useLeadMutate();
    const { addressLookup, lookupResults } = useAddressLookup();

    return {
      addAlert,
      leads: useLeadsQuery.leadsIncludingSingleLead,
      encryptSymbols,
      updateLead,
      addressLookup,
      lookupResults,
    };
  },
  data() {
    return {
      isSaving: false,
      jobName: '',
      deliveryAddress: '',
      bwQuote: '',
      complete: false,
      showComplete: false,
      notes: '',
      invNum: '',
      amount: '$',
      hasDelivery: false,
      deliveryDate: '',
      gateCode: '',
      deliveryNotes: '',
    };
  },
  methods: {
    async saveDetails() {
      this.isSaving = true;
      const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
      if (!lead) {
        this.addAlert('Error saving details', 'high', 4);
        this.$emit('closePopup');
        return;
      }

      const updatedDetails = {
        JOBNAME: this.jobName,
        DELIVERYADDRESS: this.deliveryAddress,
        BWQUOTE: this.bwQuote,
        NOTES: this.notes,
        AMOUNT: this.amount.replace(/[^0-9.]/g, ''),
      };

      if (this.showComplete) {
        if (this.complete) {
          updatedDetails.INVNUM = this.invNum;
        } else {
          updatedDetails.COMPLETE = this.complete;
          updatedDetails.COMPLETEDATE = null;
        }
      }

      if (this.hasDelivery) {
        updatedDetails.HASDELIVERY = this.hasDelivery;
        updatedDetails.DELIVERYADDRESS = this.deliveryAddress;
        updatedDetails.DELIVERYDATE = new Date(this.deliveryDate).getTime().toString();
        updatedDetails.GATECODE = this.gateCode;
        updatedDetails.DELIVERYNOTES = this.deliveryNotes;
      } else {
        updatedDetails.HASDELIVERY = false;
        updatedDetails.DELIVERYADDRESS = null;
        updatedDetails.DELIVERYDATE = null;
        updatedDetails.GATECODE = null;
        updatedDetails.DELIVERYNOTES = null;
      }

      await this.updateLead(this.leadid, this.encryptSymbols(updatedDetails));

      lead.JOBNAME = this.jobName;
      lead.BWQUOTE = this.bwQuote;
      lead.NOTES = this.notes;

      if (this.showComplete) {
        lead.COMPLETE = this.complete;
        lead.INVNUM = this.invNum;
        lead.AMOUNT = this.amount.replace(/[^0-9.]/g, '');
      }

      if (this.hasDelivery) {
        lead.HASDELIVERY = this.hasDelivery;
        lead.DELIVERYADDRESS = this.deliveryAddress;
        lead.DELIVERYDATE = (new Date(this.deliveryDate).getTime() + 8 * 60 * 60 * 1000).toString() || null;
        lead.GATECODE = this.gateCode;
        lead.DELIVERYNOTES = this.deliveryNotes;
      } else {
        lead.HASDELIVERY = false;
        lead.DELIVERYADDRESS = null;
        lead.DELIVERYDATE = null;
        lead.GATECODE = null;
        lead.DELIVERYNOTES = null;
      }

      this.isSaving = false;
      this.addAlert('Updated details', 'low', 3);

      this.$emit('closePopup');
    },
    formatAmount() {
      let newAmount = this.amount.replace(/[^0-9.]/g, '');
      if (newAmount.length == 0) {
        this.amount = '$';
        return;
      }
      newAmount = newAmount.replace(/\.(?=.*\.)/g, '');
      if (newAmount.includes('.') && newAmount.split('.')[1].length == 0) newAmount += '00';
      if (newAmount.includes('.') && newAmount.split('.')[1].length == 1) newAmount += '0';
      if (newAmount.includes('.') && newAmount.split('.')[1].length > 2) {
        newAmount = newAmount.replace(/\./g, '');
        newAmount = newAmount.slice(0, -2) + '.' + newAmount.slice(-2);
      }
      if (newAmount.startsWith('.')) newAmount = '0' + newAmount;
      if (!newAmount.includes('.')) {
        newAmount += '.00';
      }
      if (newAmount == '.00') newAmount = '0.00';
      if (newAmount.match(/^0+(?!\.|$)/)) {
        newAmount = newAmount.replace(/^0+(?!\.|$)/, '');
      }
      newAmount = '$' + newAmount;

      this.amount = newAmount;
    },
    calculateWindowDimensions() {
      this.$nextTick(() => {
        const newHeight = this.$refs.popupContent.scrollHeight + 'px';
        this.$refs.popupWindow.style.height = newHeight;
      });
    },

    getISODate(date) {
      return date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;
    },
  },
  mounted() {
    const lead = this.leads.find((lead) => lead.LEADID === this.leadid);
    if (!lead) {
      this.addAlert('Error loading details', 'high', 4);
      this.$emit('closePopup');
      return;
    }

    if (lead.COMPLETE) {
      this.showComplete = true;
      this.complete = lead.COMPLETE;
      this.invNum = lead.INVNUM;
    }

    this.amount = lead.AMOUNT;
    if (this.amount) this.formatAmount();
    else this.amount = '$';
    this.jobName = lead.JOBNAME;
    this.deliveryAddress = lead.DELIVERYADDRESS;
    this.bwQuote = lead.BWQUOTE;
    this.notes = lead.NOTES;
    this.hasDelivery = lead.HASDELIVERY;
    if (lead.DELIVERYDATE) this.deliveryDate = this.getISODate(new Date(parseInt(lead.DELIVERYDATE)));
    this.gateCode = lead.GATECODE;
    this.deliveryNotes = lead.DELIVERYNOTES;
    this.calculateWindowDimensions();
  },
  updated() {
    this.$refs.popupWindow.style.transition = 'height 0.5s ease';
    this.calculateWindowDimensions();
  },
  watch: {
    deliveryAddress() {
      this.addressLookup(this.deliveryAddress);
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-details-popup.small-popup {
  width: 40rem;
  max-height: 90vh;
  max-width: 90vw;
  overflow-y: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  .popup-body {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1.5rem 1rem;
    margin-bottom: 1.5rem;

    .input-item {
      min-width: 20rem;
      flex: 1 0 45%;

      &.small-item {
        min-width: 5rem;
        flex: 1 0 10%;
      }

      &.medium-item {
        min-width: 10rem;
        flex: 1 0 40%;
      }

      &.large-item {
        min-width: 20rem;
        flex: 1 0 90%;
      }
    }
  }
}
</style>
